<!--
  Component: FrameworkRatingFootnote

  This component creates a footnote with text based on selected footnoteType
  and if that footnote applies for the current school.

  Props:
  - footnoteType - should be one of constants in FR_FOOTNOTE_TYPES
-->

<template>
  <div :class="['framework-rating-footnote footnote',footnoteType]">
    <template v-if="isValidFootnoteType">
      <template v-if="isFootnoteECorD75">
        There are no ratings for {{ SCHOOL_TYPE_IN_FOOTNOTE[selectedSchoolType] }} schools for {{ schoolYearRange }}.
      </template>
      <template v-if="isFootnoteNewOrPhasingout">
        There are no ratings for this school because it is a new school or it is phasing out grades.
      </template>
      <template v-if="isFootnoteHighOptOut">
        †This school received no Instruction and Performance rating because less than 25% of eligible students took the state exams.
      </template>
      <template v-if="isFootnoteNoGraduatingClass">
        †This school received no Instruction and Performance rating because this school did not have a graduation rate.
      </template>
      <template v-if="isFootnoteSuppressRForSS">
        ‡This school received no Safety and School Climate rating and/or no Relationships with Families rating because this school did not have enough responses on the NYC School Survey.
      </template>
    </template>
    <template v-else>
      WARNING: INVALID FOOTNOTE TYPE - {{ `${this.footnoteType}` }}
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Component from 'vue-class-component'

import { NS_REPORT_GROUP, NS_CURRENT_REPORT } from './const'
import { devOnlyLog } from '@/utils/log'

/* Hyphenated b/c these are also used in the class name for the template */
export const FR_FOOTNOTE_TYPES = Object.freeze({
  // new in 2024
  // both EC_OR_D75 and NEW_OR_PHASEOUT get no ratings for all tabs but the footnote languages are slightly different
  EC_OR_D75: 'is-ec-or-d75',
  NEW_OR_PHASEOUT: 'is-new-or-phaseout-school',
  NO_GRADUATING_CLASS: 'no-graduating-class',
  SUPPRESS_RF_OR_SS: 'suppress-rf-or-ss',
  // existed before 2024 and still in use 2024
  OPT_OUT: 'high-opt-out-sch'
})

// Local constant
const SCHOOL_TYPE_IN_FOOTNOTE = Object.freeze({
  'EC': 'early childhood',
  'D75': 'district 75'
})

@Component({
  props: {
    footnoteType: String
  },
  computed: {
    ...mapGetters(NS_REPORT_GROUP, [
      'schoolYearRange'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, [
      'selectedSchoolType',
      'isECorD75SchoolType',
      'isNewOrPhaseoutSchool',
      'isHighOptOutSchool',
      'isNoGraduatingClassSchool',
      'suppress_ss',
      'suppress_rf'
    ])
  }
})
export default class FrameworkRatingFootnote extends Vue {
  get isValidFootnoteType () {
    return Object.values(FR_FOOTNOTE_TYPES).includes(this.footnoteType)
  }
  // No rating below is only for EC and D75
  get isFootnoteECorD75 () {
    return this.isECorD75SchoolType &&
      this.footnoteType === FR_FOOTNOTE_TYPES.EC_OR_D75
  }

  get isFootnoteNewOrPhasingout () {
    return this.isNewOrPhaseoutSchool &&
      this.footnoteType === FR_FOOTNOTE_TYPES.NEW_OR_PHASEOUT
  }

  get isFootnoteHighOptOut () {
    return this.isHighOptOutSchool && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType &&
      this.footnoteType === FR_FOOTNOTE_TYPES.OPT_OUT
  }

  get isFootnoteNoGraduatingClass () {
    return this.isNoGraduatingClassSchool && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType &&
      this.footnoteType === FR_FOOTNOTE_TYPES.NO_GRADUATING_CLASS
  }

  get isFootnoteSuppressRForSS () {
    return (this.suppress_ss || this.suppress_rf) && !this.isNewOrPhaseoutSchool && !this.isECorD75SchoolType &&
      this.footnoteType === FR_FOOTNOTE_TYPES.SUPPRESS_RF_OR_SS
  }

  created () {
    this.SCHOOL_TYPE_IN_FOOTNOTE = SCHOOL_TYPE_IN_FOOTNOTE
    if (!this.isValidFootnoteType) devOnlyLog(`WARNING: Invalid footnote type in FrameworkRatingFootnote.vue - ${this.footnoteType}`)
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/sqs/2025/FrameworkRatingFootnote.vue